import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { inr, fmtDate } from "@/lib/utils-format";
import { Search, TrendingUp, Wallet, Users, AlertTriangle, ShieldAlert, Building2, FileDown } from "lucide-react";

const AGING_LABELS = {
  current: "0-15d",
  d15_30:  "15-30d",
  d30_60:  "30-60d",
  d60_90:  "60-90d",
  d90_plus:"90+ days",
};

const AGING_COLORS = {
  current: "bg-emerald-100 text-emerald-800 border-emerald-300",
  d15_30:  "bg-amber-100 text-amber-800 border-amber-300",
  d30_60:  "bg-orange-100 text-orange-800 border-orange-300",
  d60_90:  "bg-red-100 text-red-800 border-red-300",
  d90_plus:"bg-red-200 text-red-900 border-red-400",
};

// ==============================================================
// COLLECTIONS DASHBOARD — Chief Accountant KPI summary
// ==============================================================
export function CollectionsDashboardPanel() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/receivables/collections-dashboard");
      setD(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  if (!d) return <div className="text-[#8B7355] text-sm">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="collections-dashboard">
      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-[#EAE5D9] rounded-lg p-5" data-testid="kpi-total-receivables">
          <div className="uc-label flex items-center gap-2"><Wallet className="w-3 h-3" /> Total Receivables</div>
          <div className="font-[Outfit] text-3xl font-semibold text-[#C05621] mt-1 tracking-tight">{inr(d.total_receivables)}</div>
          <div className="text-[10px] text-[#8B7355] mt-1">All open invoices across every branch</div>
        </div>
        <div className="bg-white border border-[#EAE5D9] rounded-lg p-5" data-testid="kpi-90plus">
          <div className="uc-label flex items-center gap-2"><ShieldAlert className="w-3 h-3 text-red-500" /> 90+ Days Late</div>
          <div className="font-[Outfit] text-2xl font-semibold text-red-700 mt-1">{inr(d.aging?.d90_plus)}</div>
          <div className="text-[10px] text-[#8B7355] mt-1">Danger zone — escalate</div>
        </div>
        <div className="bg-white border border-[#EAE5D9] rounded-lg p-5" data-testid="kpi-collected-mtd">
          <div className="uc-label flex items-center gap-2"><TrendingUp className="w-3 h-3 text-emerald-600" /> Collected this month</div>
          <div className="font-[Outfit] text-2xl font-semibold text-emerald-700 mt-1">{inr(d.collected_this_month)}</div>
          <div className="text-[10px] text-[#8B7355] mt-1">Payments received MTD</div>
        </div>
        <div className="bg-white border border-[#EAE5D9] rounded-lg p-5" data-testid="kpi-top-cust">
          <div className="uc-label flex items-center gap-2"><Users className="w-3 h-3" /> Top debtor</div>
          <div className="font-[Outfit] text-lg font-semibold mt-1 truncate">{d.top_customers?.[0]?.name || "—"}</div>
          <div className="text-xs text-red-700 font-semibold mt-1">{inr(d.top_customers?.[0]?.outstanding || 0)}</div>
        </div>
      </div>

      {/* Aging distribution */}
      <div className="bg-white border border-[#EAE5D9] rounded-lg p-4">
        <div className="uc-label mb-3">Aging distribution</div>
        <div className="grid grid-cols-5 gap-3">
          {Object.entries(AGING_LABELS).map(([key, label]) => {
            const amt = d.aging?.[key] || 0;
            const pct = d.total_receivables ? (amt / d.total_receivables * 100).toFixed(1) : 0;
            return (
              <div key={key} className={`border rounded-md p-3 ${AGING_COLORS[key]}`} data-testid={`aging-${key}`}>
                <div className="text-[10px] font-semibold uppercase tracking-wide">{label}</div>
                <div className="font-[Outfit] text-lg font-bold mt-1">{inr(amt)}</div>
                <div className="text-[10px] opacity-70">{pct}% of total</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* By branch and by customer type */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-hidden">
          <div className="px-4 py-2 uc-label bg-[#FAF6EE] border-b border-[#EAE5D9] flex items-center gap-2"><Building2 className="w-3 h-3" /> By Branch / Showroom</div>
          {(d.by_branch || []).length === 0 && <div className="p-3 text-xs text-[#8B7355]">Nothing outstanding</div>}
          {(d.by_branch || []).map((b) => (
            <div key={b.branch_id} className="flex items-center px-4 py-2 border-b border-[#EAE5D9] last:border-0 text-sm" data-testid={`branch-${b.branch_id}`}>
              <span className="flex-1">{b.branch_name}</span>
              <span className="text-xs text-[#8B7355] mr-3">{b.invoice_count} inv</span>
              <span className="font-semibold">{inr(b.outstanding)}</span>
            </div>
          ))}
        </div>
        <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-hidden">
          <div className="px-4 py-2 uc-label bg-[#FAF6EE] border-b border-[#EAE5D9] flex items-center gap-2"><Users className="w-3 h-3" /> By Customer Type</div>
          {(d.by_customer_type || []).length === 0 && <div className="p-3 text-xs text-[#8B7355]">Nothing outstanding</div>}
          {(d.by_customer_type || []).map((t) => (
            <div key={t.customer_type} className="flex items-center px-4 py-2 border-b border-[#EAE5D9] last:border-0 text-sm" data-testid={`ctype-${t.customer_type}`}>
              <Badge variant="outline" className="capitalize text-[10px] mr-2">{t.customer_type}</Badge>
              <span className="flex-1"></span>
              <span className="text-xs text-[#8B7355] mr-3">{t.invoice_count} inv</span>
              <span className="font-semibold">{inr(t.outstanding)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Top 10 debtors */}
      <div>
        <div className="uc-label mb-2">Top 10 debtors</div>
        <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left uc-label border-b border-[#EAE5D9]">
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3 text-right">Outstanding</th>
                <th className="px-4 py-3 text-right">Oldest days</th>
                <th className="px-4 py-3 text-right">Credit limit</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {(d.top_customers || []).map((c) => (
                <tr key={c.customer_id} className="border-b border-[#EAE5D9] last:border-0" data-testid={`top-cust-${c.customer_id}`}>
                  <td className="px-4 py-2">
                    <div className="font-medium">{c.name}</div>
                    {c.company && <div className="text-[10px] text-[#8B7355]">{c.company}</div>}
                  </td>
                  <td className="px-4 py-2 text-right font-semibold text-red-700">{inr(c.outstanding)}</td>
                  <td className="px-4 py-2 text-right">
                    <Badge variant="outline" className={c.oldest_days >= 60 ? "text-red-600 border-red-300" : c.oldest_days >= 30 ? "text-orange-600 border-orange-300" : ""}>{c.oldest_days}d</Badge>
                  </td>
                  <td className="px-4 py-2 text-right text-xs">{c.credit_limit > 0 ? inr(c.credit_limit) : "—"}</td>
                  <td className="px-4 py-2">
                    {c.over_limit ? <Badge className="bg-red-600 text-white">Over limit</Badge> : <Badge variant="outline" className="text-[10px]">OK</Badge>}
                  </td>
                </tr>
              ))}
              {(d.top_customers || []).length === 0 && (
                <tr><td colSpan={5} className="text-center py-8 text-[#8B7355]">No outstanding invoices 🎉</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ==============================================================
// OUTSTANDING (Aging) PANEL
// ==============================================================
export function OutstandingPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ageFilter, setAgeFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (ageFilter !== "all") params.aging = ageFilter;
      if (typeFilter !== "all") params.customer_type = typeFilter;
      const { data } = await api.get("/receivables/outstanding", { params });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ageFilter, typeFilter]);

  const rows = (data?.rows || []).filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (r.name || "").toLowerCase().includes(q) || (r.company || "").toLowerCase().includes(q);
  });
  const t = data?.totals || {};

  return (
    <div className="space-y-4" data-testid="outstanding-panel">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label>Aging bucket</Label>
          <Select value={ageFilter} onValueChange={setAgeFilter}>
            <SelectTrigger className="w-44" data-testid="out-age-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All buckets</SelectItem>
              {Object.entries(AGING_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Customer type</Label>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-40" data-testid="out-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="retail">Retail</SelectItem>
              <SelectItem value="b2b">B2B</SelectItem>
              <SelectItem value="distributor">Distributor</SelectItem>
              <SelectItem value="wholesale">Wholesale</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[240px]">
          <Label>Search</Label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-[#8B7355]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" placeholder="Customer / company…" data-testid="out-search" />
          </div>
        </div>
        <div className="ml-auto text-xs text-[#8B7355]">
          {loading ? "Loading…" : `${rows.length} of ${t.customers ?? 0} customers · Total ${inr(t.total_outstanding)}`}
          {t.over_limit_count > 0 && <span className="ml-2 text-red-600 font-semibold">· {t.over_limit_count} over limit</span>}
        </div>
      </div>

      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left uc-label border-b border-[#EAE5D9]">
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3 text-right">0-15d</th>
              <th className="px-4 py-3 text-right">15-30</th>
              <th className="px-4 py-3 text-right">30-60</th>
              <th className="px-4 py-3 text-right">60-90</th>
              <th className="px-4 py-3 text-right">90+</th>
              <th className="px-4 py-3 text-right">Total due</th>
              <th className="px-4 py-3">Credit</th>
            </tr>
          </thead>
          <tbody data-testid="out-rows">
            {rows.map((r) => (
              <React.Fragment key={r.customer_id}>
                <tr
                  onClick={() => setExpanded({ ...expanded, [r.customer_id]: !expanded[r.customer_id] })}
                  className={`border-b border-[#EAE5D9] cursor-pointer hover:bg-[#FDFBF7] ${expanded[r.customer_id] ? "bg-[#FEEBC8]" : ""}`}
                  data-testid={`out-row-${r.customer_id}`}
                >
                  <td className="px-4 py-2">
                    <div className="font-medium">{r.name}</div>
                    <div className="text-[10px] text-[#8B7355]">{r.company || "—"} · <span className="capitalize">{r.customer_type}</span> · {r.invoice_count} open inv</div>
                  </td>
                  <td className="px-4 py-2 text-right text-xs">{r.aging.current ? inr(r.aging.current) : "—"}</td>
                  <td className={`px-4 py-2 text-right text-xs ${r.aging.d15_30 ? "font-semibold text-amber-700" : ""}`}>{r.aging.d15_30 ? inr(r.aging.d15_30) : "—"}</td>
                  <td className={`px-4 py-2 text-right text-xs ${r.aging.d30_60 ? "font-semibold text-orange-700" : ""}`}>{r.aging.d30_60 ? inr(r.aging.d30_60) : "—"}</td>
                  <td className={`px-4 py-2 text-right text-xs ${r.aging.d60_90 ? "font-semibold text-red-700" : ""}`}>{r.aging.d60_90 ? inr(r.aging.d60_90) : "—"}</td>
                  <td className={`px-4 py-2 text-right text-xs ${r.aging.d90_plus ? "font-bold text-red-800" : ""}`}>{r.aging.d90_plus ? inr(r.aging.d90_plus) : "—"}</td>
                  <td className="px-4 py-2 text-right font-semibold text-red-700">{inr(r.outstanding)}</td>
                  <td className="px-4 py-2">
                    {r.credit_limit > 0 ? (
                      <div>
                        <div className="text-[10px] text-[#8B7355]">{inr(r.credit_limit)}</div>
                        <div className={`text-[10px] font-semibold ${r.over_limit ? "text-red-600" : r.credit_utilization_pct > 80 ? "text-amber-700" : "text-emerald-700"}`}>
                          {r.credit_utilization_pct}% used
                        </div>
                      </div>
                    ) : <span className="text-[10px] text-[#8B7355]">no limit</span>}
                  </td>
                </tr>
                {expanded[r.customer_id] && (
                  <tr>
                    <td colSpan={8} className="bg-[#FAF6EE] px-6 py-3">
                      <div className="uc-label mb-1">Open invoices</div>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-[#8B7355]">
                            <th className="text-left py-1">#</th>
                            <th className="text-left py-1">Date</th>
                            <th className="text-right py-1">Amount</th>
                            <th className="text-right py-1">Paid</th>
                            <th className="text-right py-1">Due</th>
                            <th className="text-right py-1">Days</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.invoices.map((i) => (
                            <tr key={i.id} data-testid={`out-inv-${i.id}`}>
                              <td className="py-1 font-mono">{i.number}</td>
                              <td className="py-1">{fmtDate(i.created_at)}</td>
                              <td className="py-1 text-right">{inr(i.grand_total)}</td>
                              <td className="py-1 text-right">{inr(i.amount_paid)}</td>
                              <td className="py-1 text-right font-semibold text-red-700">{inr(i.due)}</td>
                              <td className="py-1 text-right">
                                <Badge variant="outline" className={`${AGING_COLORS[i.bucket]} text-[10px]`}>{i.days}d</Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-[#8B7355]">{loading ? "Loading…" : "No outstanding invoices match this filter 🎉"}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ==============================================================
// ENHANCED CUSTOMER STATEMENT (replaces old bare Customer Ledger tab)
// ==============================================================
export function CustomerStatementPanel() {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [stmt, setStmt] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  const load = async (cid) => {
    if (!cid) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/receivables/statement/${cid}`);
      setStmt(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4" data-testid="customer-statement-panel">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[300px]">
          <Label>Customer</Label>
          <Select value={customerId} onValueChange={(v) => { setCustomerId(v); load(v); }}>
            <SelectTrigger data-testid="stmt-customer-select"><SelectValue placeholder="Pick a customer" /></SelectTrigger>
            <SelectContent className="bg-white max-h-96">
              {customers.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}{c.company ? ` · ${c.company}` : ""}{c.customer_type ? ` · ${c.customer_type}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading && <div className="text-sm text-[#8B7355]">Loading…</div>}

      {stmt && (
        <>
          {/* Customer summary strip */}
          <div className="bg-white border border-[#EAE5D9] rounded-lg p-4 grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="stmt-summary">
            <div>
              <div className="uc-label">Customer</div>
              <div className="font-[Outfit] text-xl font-semibold">{stmt.customer.name}</div>
              {stmt.customer.company && <div className="text-xs text-[#8B7355]">{stmt.customer.company}</div>}
              <Badge variant="outline" className="mt-1 text-[10px] capitalize">{stmt.customer.customer_type || "retail"}</Badge>
            </div>
            <div>
              <div className="uc-label">Total billed</div>
              <div className="font-[Outfit] text-xl font-semibold">{inr(stmt.totals.total_billed)}</div>
            </div>
            <div>
              <div className="uc-label">Total paid</div>
              <div className="font-[Outfit] text-xl font-semibold text-emerald-700">{inr(stmt.totals.total_paid)}</div>
            </div>
            <div>
              <div className="uc-label">Outstanding</div>
              <div className={`font-[Outfit] text-xl font-semibold ${stmt.totals.outstanding > 0 ? "text-red-700" : "text-emerald-700"}`}>{inr(stmt.totals.outstanding)}</div>
            </div>
            <div>
              <div className="uc-label">Credit limit</div>
              <div className="font-[Outfit] text-xl font-semibold">{stmt.totals.credit_limit ? inr(stmt.totals.credit_limit) : "—"}</div>
              {stmt.totals.credit_limit ? (
                <div className={`text-xs font-semibold mt-1 ${stmt.totals.over_limit ? "text-red-600" : stmt.totals.credit_utilization_pct > 80 ? "text-amber-700" : "text-emerald-700"}`}>
                  {stmt.totals.credit_utilization_pct}% used
                  {stmt.totals.over_limit && <span className="ml-1"><AlertTriangle className="w-3 h-3 inline" /> OVER LIMIT</span>}
                </div>
              ) : <div className="text-[10px] text-[#8B7355] mt-1">No limit set</div>}
            </div>
          </div>

          {/* Entries with running balance */}
          <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left uc-label border-b border-[#EAE5D9]">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Ref</th>
                  <th className="px-4 py-3">Narration</th>
                  <th className="px-4 py-3 text-right">Debit</th>
                  <th className="px-4 py-3 text-right">Credit</th>
                  <th className="px-4 py-3 text-right">Balance</th>
                </tr>
              </thead>
              <tbody data-testid="stmt-rows">
                {stmt.entries.map((e, i) => (
                  <tr key={i} className="border-b border-[#EAE5D9] last:border-0">
                    <td className="px-4 py-2 text-[#8B7355] text-xs">{fmtDate(e.when)}</td>
                    <td className="px-4 py-2">
                      <Badge variant="outline" className={`capitalize text-[10px] ${e.kind === "invoice" ? "border-red-300 text-red-700" : "border-emerald-300 text-emerald-700"}`}>
                        {e.kind}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">{e.ref}</td>
                    <td className="px-4 py-2 text-[#5C4033] max-w-[300px] truncate">{e.narration}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs text-red-700">{e.debit > 0 ? inr(e.debit) : "—"}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs text-emerald-700">{e.credit > 0 ? inr(e.credit) : "—"}</td>
                    <td className={`px-4 py-2 text-right font-mono font-semibold ${e.balance > 0 ? "text-red-700" : "text-emerald-700"}`}>{inr(e.balance)}</td>
                  </tr>
                ))}
                {stmt.entries.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-[#8B7355]">No transactions for this customer.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
